/**
 * Provider abstraction for link previews.
 *
 * A provider owns one family of URLs and knows three things about it: whether a
 * URL belongs to it, what preview can be derived from the address alone, and
 * where to ask for richer metadata. `webpage` is the catch-all and answers for
 * everything nobody else claims.
 *
 *     LinkPreview
 *     ├── webpage   (Open Graph / Twitter card / <title>)
 *     ├── youtube   (video id → thumbnail + oEmbed title)
 *     └── …         (add a provider below; nothing else changes)
 *
 * Adding a provider — Vimeo, say — is: write the object, list it in
 * `LINK_PREVIEW_PROVIDERS` before `webpageProvider`, add its `type` to
 * `LINK_PREVIEW_TYPES` in the model, and give the card a branch. Extraction,
 * caching, persistence and the security checks are provider-independent.
 *
 * No React Native imports: the Cloudflare Worker in `workers/link-preview`
 * imports this module too.
 */

import { LinkPreview, LinkPreviewType } from "../models/link-preview.model";
import { getDomain } from "./link-preview.utils";

export type LinkPreviewProvider = {
  type: LinkPreviewType;
  /** Does this provider own the URL? */
  matches: (url: URL) => boolean;
  /** Provider media id (a YouTube video id), when the provider has one. */
  extractMediaId?: (url: URL) => string | null;
  /**
   * The preview derivable without fetching anything. Used as the starting point
   * for the extractor and as the fallback when metadata extraction fails, so a
   * YouTube link still renders a thumbnail when the network does not cooperate.
   */
  buildFallback: (url: URL) => LinkPreview | null;
  /** oEmbed endpoint for richer metadata, when the provider publishes one. */
  oEmbedUrl?: (url: URL) => string | null;
};

/* ------------------------------------------------------------------ YouTube */

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
  "youtu.be",
  "www.youtu.be",
]);

/** YouTube ids are exactly 11 url-safe characters. Anything else is not an id. */
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

/** Path prefixes that carry the id as the next segment. */
const YOUTUBE_PATH_PREFIXES = ["shorts", "embed", "live", "v"];

export const extractYoutubeVideoId = (url: URL): string | null => {
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  if (!YOUTUBE_HOSTS.has(host)) return null;

  const segments = url.pathname.split("/").filter(Boolean);

  // youtu.be/<id>
  if (host.endsWith("youtu.be")) {
    const id = segments[0];
    return id && YOUTUBE_ID.test(id) ? id : null;
  }

  // youtube.com/watch?v=<id>
  if (segments[0] === "watch") {
    const id = url.searchParams.get("v");
    return id && YOUTUBE_ID.test(id) ? id : null;
  }

  // youtube.com/{shorts,embed,live,v}/<id>
  if (YOUTUBE_PATH_PREFIXES.includes(segments[0])) {
    const id = segments[1];
    return id && YOUTUBE_ID.test(id) ? id : null;
  }

  return null;
};

/**
 * `hqdefault` rather than `maxresdefault`: the high-res variant is only
 * generated for some uploads and 404s for the rest, which would put a broken
 * image in the feed for exactly the videos that are hardest to notice.
 */
export const youtubeThumbnailUrl = (videoId: string): string =>
  `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

/** The canonical watch URL, so `youtu.be` and `/embed/` links open the same page. */
export const youtubeWatchUrl = (videoId: string): string =>
  `https://www.youtube.com/watch?v=${videoId}`;

const youtubeProvider: LinkPreviewProvider = {
  type: "youtube",
  matches: (url) => extractYoutubeVideoId(url) !== null,
  extractMediaId: extractYoutubeVideoId,
  buildFallback: (url) => {
    const videoId = extractYoutubeVideoId(url);
    if (!videoId) return null;
    return {
      url: url.toString(),
      type: "youtube",
      title: null,
      description: null,
      imageUrl: youtubeThumbnailUrl(videoId),
      siteName: "YouTube",
      domain: "youtube.com",
      videoId,
    };
  },
  oEmbedUrl: (url) => {
    const videoId = extractYoutubeVideoId(url);
    if (!videoId) return null;
    return `https://www.youtube.com/oembed?url=${encodeURIComponent(
      youtubeWatchUrl(videoId),
    )}&format=json`;
  },
};

/* ------------------------------------------------------------------ Webpage */

const webpageProvider: LinkPreviewProvider = {
  type: "webpage",
  matches: () => true,
  // Nothing is known before the page is fetched — an empty card is worse than
  // the plain link the body text already shows.
  buildFallback: () => null,
};

/** Ordered: the first provider that claims the URL wins, catch-all last. */
export const LINK_PREVIEW_PROVIDERS: readonly LinkPreviewProvider[] = [
  youtubeProvider,
  webpageProvider,
];

export const resolveProvider = (url: URL): LinkPreviewProvider =>
  LINK_PREVIEW_PROVIDERS.find((provider) => provider.matches(url)) ??
  webpageProvider;

/** The provider `type` a URL will preview as, without fetching anything. */
export const detectLinkPreviewType = (raw: string): LinkPreviewType | null => {
  try {
    return resolveProvider(new URL(raw)).type;
  } catch {
    return null;
  }
};

/**
 * Preview derivable from the address alone, or null when the provider needs to
 * see the page first. Lets a YouTube card render instantly and survive a failed
 * metadata fetch.
 */
export const buildProviderFallback = (raw: string): LinkPreview | null => {
  try {
    const url = new URL(raw);
    return resolveProvider(url).buildFallback(url);
  } catch {
    return null;
  }
};

/** An empty webpage preview: the shape every extractor result starts from. */
export const emptyWebpagePreview = (url: string): LinkPreview => ({
  url,
  type: "webpage",
  title: null,
  description: null,
  imageUrl: null,
  siteName: null,
  domain: getDomain(url),
  videoId: null,
});
